import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { TimeRange} from "@@/shared/date-time/time-range";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import type { DurationInMinutes } from "@@/shared/date-time/time-spans";
import type { DiscountId} from "@@/shared/discounts/discount-id";
import { discountIdFactory } from "@@/shared/discounts/discount-id";
import type { EntityState} from "@@/shared/entity-status";
import { entityStateZodSchema } from "@@/shared/entity-status";
import type {
    MetaContent} from "@@/shared/meta-content/meta-content";
import {
    metaContentZodSchema,
} from "@@/shared/meta-content/meta-content";
import type { OwnerId} from "@@/shared/owner-id";
import { ownerIdZodSchema } from "@@/shared/owner-id";
import type { Price} from "@@/shared/prices/price";
import { priceZodSchema } from "@@/shared/prices/price";
import type { UserId} from "@@/shared/users/user-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Branded } from "@@/utilities/branded";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    DiscountCondition} from "../../conditions/discount-condition";
import {
    discountConditionZodSchema,
} from "../../conditions/discount-condition";

type TimePriceStepDiscountType = "TIME_PRICE_STEP_DISCOUNT";

//Replaces the price to the price at the different step values
type TimePriceStepDiscount = {
    _id: DiscountId;
    _createdAt: IsoAndUnixTimestamp;
    _createdBy: UserId;
    _status: EntityState;
    type: TimePriceStepDiscountType;
    ownerId: OwnerId;
    validTimeRanges: TimeRange[];
    meta: MetaContent;
    conditions: DiscountCondition[];
    //OverThisTimeThatPrice
    steps: TimePriceStep[];
};

type TimeStepId = Branded<string, "TimeStepId">;
const timeStepIdFactory = (): TimeStepId =>
    generateId({ prefix: "time_step__" });

const timeStepIdZodSchema = z
    .string()
    .regex(/^time_step_.*$/) as unknown as z.ZodSchema<TimeStepId>;

type TimePriceStep = {
    _id?: string;
    step: DurationInMinutes;
    amountIncludingVat: number;
    price?: Price;
};

const timePriceStepZodSchema: z.ZodSchema<TimePriceStep> = z.object({
    _id: timeStepIdZodSchema.optional(),
    step: z.coerce.number().min(0) as unknown as z.ZodSchema<DurationInMinutes>,
    amountIncludingVat: z.coerce.number().min(0),
    price: priceZodSchema?.optional(),
});

type FactoryParams = Except<
    TimePriceStepDiscount,
    "_id" | "_createdAt" | "type"
>;
const timePriceStepDiscountFactory = (
    params: FactoryParams,
): TimePriceStepDiscount => {
    return {
        ...params,
        _id: discountIdFactory(),
        _createdAt: nowIsoAndUnix(),
        type: "TIME_PRICE_STEP_DISCOUNT",
    };
};

const timePriceStepDiscountZodObject = z.object({
    _id: z.string() as unknown as z.ZodSchema<DiscountId>,
    _createdAt: isoAndUnixZodSchema,
    _createdBy: userIdZodSchema,
    _status: entityStateZodSchema,
    meta: metaContentZodSchema,
    ownerId: ownerIdZodSchema,
    type: z.literal("TIME_PRICE_STEP_DISCOUNT"),
    validTimeRanges: z.array(timeRangeZodSchema),
    conditions: z.array(discountConditionZodSchema),
    steps: z.array(timePriceStepZodSchema),
});
const timePriceStepDiscountZodSchema: z.ZodSchema<TimePriceStepDiscount> =
    timePriceStepDiscountZodObject;

export {
    timePriceStepDiscountFactory,
    timePriceStepDiscountZodObject,
    timePriceStepDiscountZodSchema,
    timePriceStepZodSchema,
    timeStepIdFactory,
};
export type { TimePriceStep, TimePriceStepDiscount, TimePriceStepDiscountType };
